import {
  getTargetStatusLabel,
  targetStatusKey,
  type TargetStatusKey,
} from "@/v2-backoffice-client/uptime/uptime.utils"
import type { UptimeInlineFragment } from "@/v2-backoffice-client/uptime/inline/__types__/uptime.inline.fragment"

type InlineStatusKey = TargetStatusKey | "drift"

/**
 * Pick a status key for display. Slightly stricter than `targetStatusKey`
 * because the inline surfaces want to show drift on otherwise-up rows (the
 * drift state has no equivalent in `LatestStatus`, but operators expect to
 * see it wherever the target's headline status is shown).
 */
export function pickInlineStatusKey(
  target: UptimeInlineFragment,
): InlineStatusKey {
  const base = targetStatusKey(target)
  if (base === "up" && (target.HasMetadataDrift || target.HasCountDrift)) {
    return "drift"
  }
  return base
}

/**
 * Label per status key. Goes through the key vocabulary (Down / Slow /
 * Skipped / Up), not the raw LatestStatus labels: a slow outlier is "up" in
 * LatestStatus terms and read as "Unknown" when it was looked up there.
 */
export function getInlineStatusLabel(key: InlineStatusKey): string {
  if (key === "drift") return "Up · drift detected"
  if (key === "up") return "Operational"
  return getTargetStatusLabel(key)
}

export function getInlineStatusVariant(
  key: InlineStatusKey,
): "success-1" | "destructive" | "warning" {
  switch (key) {
    case "down": {
      return "destructive"
    }
    case "slow":
    case "skipped": {
      return "warning"
    }
    // Drift rides on a green status visually -- the actual probes are
    // succeeding -- but the label carries the warning so it isn't lost.
    default: {
      return "success-1"
    }
  }
}
